import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { PlatformBrandIcon } from '@/components/icons/platform-brand-icon';
import {
  GoogleRsaAdPreview,
  InstagramAdPreview,
  LinkedInAdPreview,
  MetaAdPreview,
  PlatformPreview,
  PreviewStage,
  StoryAdPreview,
  platformNaturalMaxWidth,
} from '@/components/ui/platform-preview';
import { SegmentedToggle, type SegmentedToggleOption } from '@/components/ui/segmented-toggle';
import type { AdPlatformId } from '@/constants/ad-specs';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBrandKits } from '@/hooks/useBrandKits';
import { useConceptCopyVariants } from '@/hooks/useConceptCopyVariants';
import { captionFromSummary } from '@/lib/briefs/concept-labels';
import { linkedInCtaDisplayLabel } from '@/lib/paid-media/linkedin/options';
import {
  type AspectRatioToken,
  type Platform,
  getPlacementsForPlatform,
  ratioToNumber,
  toMatrixPlatform,
} from '@/lib/platform-matrix/placement-matrix';
import { cn } from '@/lib/utils';

/**
 * Maps a concept's stored delivery-intent platform string to the ad-copy
 * generator's `AdPlatformId` vocabulary. Deliberately narrower than
 * `toMatrixPlatform` — an ad has no "reel"/"story" placement axis, only a
 * network, and Google has no organic matrix row at all (search has no
 * organic post surface), so it cannot be derived from the organic matrix.
 */
function resolveAdPlatformId(platform: string | null | undefined): AdPlatformId | null {
  const p = (platform ?? '').toLowerCase().trim();
  if (p === 'facebook' || p === 'instagram' || p === 'meta') return 'meta';
  if (p === 'linkedin') return 'linkedin';
  if (p === 'google' || p === 'google_ads' || p === 'google_rsa' || p === 'search') {
    return 'google_rsa';
  }
  return null;
}

/**
 * WHICH AD SHELL DRAWS — a different question from `resolveAdPlatformId` above,
 * and deliberately a separate function.
 *
 * That one answers "whose copy vocabulary is this?", where Instagram genuinely
 * IS Meta: the ad-copy generator has no Instagram row, and an Instagram ad's
 * copy is written against Meta's fields. This one answers "whose chrome does a
 * marketer actually see?", where Instagram is NOT Facebook: different header,
 * different action row, a CTA row with no headline or destination domain, and a
 * 4:5 media crop instead of 1.91:1. Collapsing the two is how an Instagram
 * carousel concept ends up previewing as a single-image Facebook ad.
 */
export type AdShellId = 'instagram' | 'meta' | 'linkedin' | 'google_rsa';

function resolveAdShellId(platform: string | null | undefined): AdShellId | null {
  const p = (platform ?? '').toLowerCase().trim();
  if (p === 'instagram' || p === 'ig') return 'instagram';
  if (p === 'facebook' || p === 'meta' || p === 'fb') return 'meta';
  if (p === 'linkedin') return 'linkedin';
  if (p === 'google' || p === 'google_ads' || p === 'google_rsa' || p === 'search') {
    return 'google_rsa';
  }
  return null;
}

/**
 * The placement row that best matches a ratio for a platform: an exact
 * `ratioOptions` match, preferring the row whose `recommendedRatio` equals
 * it, else the first row that lists it at all, else the platform's primary
 * row (so 4:5 → feed, 9:16 → story/reel, as the founder specified) — never a
 * guess at a ratio the platform does not actually offer.
 */
export const AD_PLATFORM_OPTIONS = [
  {
    value: 'meta',
    icon: <PlatformBrandIcon platform="meta-ads" />,
    tooltip: 'Meta Ads',
    ariaLabel: 'Meta Ads',
  },
  {
    value: 'linkedin',
    icon: <PlatformBrandIcon platform="linkedin" />,
    tooltip: 'LinkedIn Ads',
    ariaLabel: 'LinkedIn Ads',
  },
  {
    value: 'google_rsa',
    icon: <PlatformBrandIcon platform="google-ads" />,
    tooltip: 'Google Ads',
    ariaLabel: 'Google Ads',
  },
] as const satisfies ReadonlyArray<SegmentedToggleOption<AdPlatformId>>;

const AD_PLATFORM_LABEL: Record<AdPlatformId, string> = {
  meta: 'Meta',
  linkedin: 'LinkedIn',
  google_rsa: 'Google Search',
};

// Paid preview chrome is measured from the replicas themselves rather than
// from the organic post metadata. The workbench is the sole consumer because
// it is the only surface that scales these cards to a definite-height column.
const PAID_PREVIEW_CHROME_RESERVE: Record<'meta' | 'linkedin', number> = {
  meta: 240,
  linkedin: 256,
};
const PAID_PREVIEW_SURROUND_RESERVE = { desktop: 136, mobile: 184 } as const;
const PAID_PREVIEW_LEGIBILITY_FLOOR = 320;
/**
 * A story ad is not a card with media plus chrome, so it does not use the
 * chrome-reserve model above: the whole 338×718 canvas IS the ad. It fits by
 * its own height, capped at the Figma frame's width.
 */
const STORY_AD_CANVAS_RATIO = 338 / 718;
const STORY_AD_NATURAL_WIDTH = 338;
const META_COPY_ONLY_RATIO = 338 / 331;
const LINKEDIN_COPY_ONLY_RATIO = 1.91;

export function storyPreviewMaxWidth(isMobile: boolean): string {
  const reservedHeight = PAID_PREVIEW_SURROUND_RESERVE[isMobile ? 'mobile' : 'desktop'];
  return `min(100%, ${STORY_AD_NATURAL_WIDTH}px, calc((100cqh - ${reservedHeight}px) * ${STORY_AD_CANVAS_RATIO.toFixed(3)}))`;
}

function paidPreviewSizingConstraints(platform: 'meta' | 'linkedin', isMobile: boolean) {
  const naturalCap = platformNaturalMaxWidth(
    platform === 'meta' ? 'facebook-post' : 'linkedin-post',
  );
  const reservedHeight =
    PAID_PREVIEW_CHROME_RESERVE[platform] +
    PAID_PREVIEW_SURROUND_RESERVE[isMobile ? 'mobile' : 'desktop'];

  return { naturalCap, reservedHeight, legibilityFloor: PAID_PREVIEW_LEGIBILITY_FLOOR };
}

/**
 * Match the ratio the ad shell renders, rather than always using the selected
 * rendition token. Copy-only cards draw their honest placeholder band at the
 * shell's native ratio; media and loading cards draw the selected rendition.
 */
export function resolvePaidPreviewMediaRatio(
  platform: AdPlatformId,
  aspectRatio: AspectRatioToken,
  hasImage: boolean,
  isLoading: boolean,
): number {
  if (hasImage || isLoading) return ratioToNumber(aspectRatio);
  switch (platform) {
    case 'meta':
      return META_COPY_ONLY_RATIO;
    case 'linkedin':
      return LINKEDIN_COPY_ONLY_RATIO;
    case 'google_rsa':
      return 1;
  }
}

/**
 * Fit a paid replica by the media height it can occupy in the workbench's
 * size container. At 320px the card stops shrinking and the pane scrolls so
 * platform chrome remains truthful and legible.
 */
export function paidPreviewMaxWidth(
  platform: 'meta' | 'linkedin',
  effectiveMediaRatio: number,
  isMobile: boolean,
): string {
  const { naturalCap, reservedHeight, legibilityFloor } = paidPreviewSizingConstraints(
    platform,
    isMobile,
  );
  const renderedRatio = Math.max(0.3, effectiveMediaRatio).toFixed(3);

  return `min(100%, ${naturalCap}px, max(${legibilityFloor}px, calc((100cqh - ${reservedHeight}px) * ${renderedRatio})))`;
}

/**
 * Numeric counterpart to `paidPreviewMaxWidth`, kept on the same constraint
 * source so tests can prove the natural cap, legibility floor, and height
 * derived branch without trying to evaluate container-query CSS in JSDOM.
 */
export function paidPreviewWidthAtHeight(
  platform: 'meta' | 'linkedin',
  effectiveMediaRatio: number,
  isMobile: boolean,
  containerHeight: number,
): number {
  const { naturalCap, reservedHeight, legibilityFloor } = paidPreviewSizingConstraints(
    platform,
    isMobile,
  );
  const heightDerivedWidth =
    (containerHeight - reservedHeight) * Math.max(0.3, effectiveMediaRatio);

  return Math.min(naturalCap, Math.max(legibilityFloor, heightDerivedWidth));
}

function resolvePlacement(platform: Platform, ratio: AspectRatioToken) {
  const rows = getPlacementsForPlatform(platform);
  if (rows.length === 0) return null;
  const exact = rows.find((row) => row.recommendedRatio === ratio);
  if (exact) return exact;
  const contains = rows.find((row) => row.ratioOptions.includes(ratio));
  if (contains) return contains;
  return rows[0];
}

export interface ConceptPreviewPaneProps {
  conceptId: string;
  /** The concept's own delivery intents — same source as the ratio matrix. */
  intents: readonly { intent: 'organic' | 'paid'; platform: string | null }[];
  imageUrl: string | null;
  /**
   * Every rendered size this Concept holds, in order — the carousel variant's
   * cards. Exactly as many entries as there are real renditions with an image;
   * the shells count what they are given and never pad to a plausible number.
   */
  imageUrls?: readonly string[];
  aspectRatio: AspectRatioToken;
  /**
   * What the empty creative band says. Composed by the caller from values that
   * were actually stored (the visual direction, the placement matrix's accepted
   * ratios) — never a shrug, and never an approximation.
   */
  emptyHint?: string;
  /** Brief fields used as an honest fallback while ad copy has not been generated yet. */
  hook: string | null;
  summary: string | null;
  cta: string | null;
  /**
   * The Design-mode overlay layers (headline/logo/shape stack), pre-rendered
   * into a chromeless composite node by the caller — see `ConceptDesignBody`'s
   * `WorkbenchStage presentation="composite"` call. This pane stays
   * presentational and does not itself depend on the variant-set store; it
   * only knows where the composite belongs (Meta/LinkedIn's `MediaSlot`, and
   * the organic `PlatformPreview`'s `mediaOverlay`), never how it's built.
   * Never passed to `GoogleRsaAdPreview` — Google Search has no image
   * placement, so there is no media region for the artwork to sit inside.
   */
  overlay?: ReactNode;
  /**
   * THE W-1 FIX. The Content section's own selection, lifted up to
   * `ConceptDesignBody` and handed down here — see `ConceptCopySection`'s and
   * `WorkbenchInspector`'s doc comments for the seam. `null`/`undefined` (no
   * selection yet, or the id no longer matches a loaded variant) falls back to
   * the same "Option 1" default `ConceptCopySection` itself uses, so the two
   * stay in agreement even before the user has clicked anything.
   */
  selectedCopyVariantId?: string | null;
  /**
   * The platform switcher, lifted.
   *
   * Supplying `onAdPlatformChange` hands the CONTROL to the parent and this
   * pane stops rendering its own inline row. It exists because the switcher
   * has to sit against the workbench well's right edge, under the
   * Design/Preview toggle (founder, 2026-08-21: _"stacked vertically and below
   * design/preview aligned against that border line… more like a toolbar"_),
   * and this pane is width-capped and centred — its right edge is nowhere near
   * the rail. Only a sibling of the scroll container can reach that edge.
   *
   * The RESOLUTION stays here rather than being duplicated upward: which
   * platform to show when the user has chosen nothing depends on the loaded
   * copy variant, which only this pane reads. `onResolvedAdPlatformChange`
   * reports it up so the parent's toggle can show the right value without
   * re-deriving it.
   */
  adPlatform?: AdPlatformId | null;
  onAdPlatformChange?: (platform: AdPlatformId) => void;
  onResolvedAdPlatformChange?: (platform: AdPlatformId) => void;
  className?: string;
}

/**
 * THE PREVIEW HALF OF THE DESIGN ⇄ PREVIEW SWITCH.
 *
 * Design mode is the raw artwork on the Konva stage; this is the same
 * artwork composed inside the platform chrome it will actually ship in —
 * the same discipline `PostBody`'s preview pane already holds for organic
 * posts, and `AdUnitPreview` holds for a saved ad unit. This pane runs off
 * DRAFT state (the concept's own fields + the selected size's live image)
 * rather than a persisted `AdUnitRecord`, which is why it composes
 * `MetaAdPreview` / `LinkedInAdPreview` / `GoogleRsaAdPreview` directly
 * instead of reusing `AdUnitPreview` itself — that component requires a
 * saved row and cannot run mid-edit.
 *
 * PLACEMENT FOLLOWS THE RATIO AND THE PLATFORM, not a fixed choice: a 4:5
 * concept on Instagram previews the feed; a 9:16 concept previews the
 * story/reel chrome. `resolvePlacement` is the one place that decision is
 * made, off the same `PLACEMENT_MATRIX` the ratio strip already reads.
 *
 * COVERAGE IS HONEST. Paid ads render only for the three platforms that
 * have a real ad-shell component (Meta, LinkedIn, Google RSA) — TikTok,
 * Pinterest, X and YouTube ads have no built shell, and this pane says so
 * rather than drawing an invented one. Organic posts cover all eight
 * `PLACEMENT_MATRIX` platforms, because every one of them already has a
 * `PlatformPreviewKind` shell.
 *
 * PAID PREVIEW IS PLATFORM-SWITCHABLE, same discipline as `PostBody`'s own
 * platform-tab strip: an underline `SegmentedToggle` (`variant="tabs"`)
 * governs which ad shell renders, defaulting to the concept's own resolved
 * platform. Switching tabs re-renders the SAME copy fields inside a
 * different network's chrome — a "how would this read on X" preview, not a
 * claim that different copy exists per platform, since one concept normally
 * has one generated variant. When the concept's own declared platform has no
 * built ad shell (or the user switches away from it), a caption says so
 * rather than staying silent about the substitution.
 */
export function ConceptPreviewPane({
  conceptId,
  intents,
  imageUrl,
  imageUrls,
  aspectRatio,
  emptyHint,
  hook,
  summary,
  cta,
  overlay,
  selectedCopyVariantId,
  adPlatform,
  onAdPlatformChange,
  onResolvedAdPlatformChange,
  className,
}: ConceptPreviewPaneProps): JSX.Element {
  const isMobile = useIsMobile();
  const { activeKit } = useBrandKits();
  const {
    variants: copyVariants,
    isLoading: copyLoading,
    refetch: refetchCopyVariants,
  } = useConceptCopyVariants(conceptId);
  // User's manual override of the switcher, above. `null` defers to the
  // concept's own resolved platform so a fresh concept opens on its native
  // network rather than always defaulting to Meta.
  const [ownSelectedAdPlatform, setOwnSelectedAdPlatform] = useState<AdPlatformId | null>(null);
  const isPlatformControlled = onAdPlatformChange !== undefined;
  const selectedAdPlatform = isPlatformControlled ? (adPlatform ?? null) : ownSelectedAdPlatform;
  const setSelectedAdPlatform = isPlatformControlled
    ? onAdPlatformChange
    : setOwnSelectedAdPlatform;

  const brandName = activeKit?.name?.trim() || 'Your brand';
  const brandColor = activeKit?.primary_color ?? undefined;
  const avatarUrl = activeKit?.logo_url ?? undefined;

  // The concept's primary delivery intent. The inspector's `Delivery` / `Paid
  // on` rows already treat this as singular (`ConceptFieldsSection`), so the
  // preview follows the same read rather than inventing multi-intent UI.
  const primaryIntent = intents[0] ?? null;
  const isPaid = primaryIntent?.intent === 'paid';
  const platformRaw = primaryIntent?.platform ?? null;

  const adPlatformId = useMemo(() => resolveAdPlatformId(platformRaw), [platformRaw]);
  const adShellId = useMemo(() => resolveAdShellId(platformRaw), [platformRaw]);
  const matrixPlatform = useMemo(() => toMatrixPlatform(platformRaw), [platformRaw]);

  // SAME query, SAME sort as `ConceptCopySection.ordered` (creation order, id
  // tiebreak), and deliberately NOT pre-filtered to the concept's declared
  // delivery platform. Filtering first and sorting second picked a different
  // underlying row than the Content tab's own default whenever the
  // earliest-created option happened not to be the earliest MATCHING-PLATFORM
  // option — a real mismatch caught live (preview showed one headline while
  // Content showed "Option 1" holding a different one). Matching the query
  // bit-for-bit is what keeps the two in agreement.
  //
  // W-1 FIX: `selectedCopyVariantId` is the id `ConceptCopySection` reports up
  // through `ConceptDesignBody` (see `ConceptPreviewPaneProps.selectedCopyVariantId`).
  // Selecting BY ID against this same ordered list — never by index into a
  // separately-filtered or separately-sorted list — is what keeps this
  // in agreement with whichever option Content's radio group actually has
  // selected. A `null`/stale/not-yet-loaded id falls back to `ordered[0]`,
  // the exact default `ConceptCopySection` itself falls back to when nothing
  // has been clicked yet.
  const adCopy = useMemo(() => {
    const ordered = copyVariants
      .slice()
      .sort((a, b) =>
        a.createdAt !== b.createdAt ? (a.createdAt < b.createdAt ? -1 : 1) : a.id < b.id ? -1 : 1,
      );
    if (selectedCopyVariantId) {
      const selected = ordered.find((variant) => variant.id === selectedCopyVariantId);
      if (selected) return selected;
    }
    return ordered[0] ?? null;
  }, [copyVariants, selectedCopyVariantId]);
  // The current option can be written after this list was read (the agent saves a new row, then
  // makes it current). An id this list does not hold means the list is stale, not that the
  // current copy is option A: read it again once per id.
  const refetchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedCopyVariantId || copyLoading) return;
    if (copyVariants.some((variant) => variant.id === selectedCopyVariantId)) return;
    if (refetchedFor.current === selectedCopyVariantId) return;
    refetchedFor.current = selectedCopyVariantId;
    void refetchCopyVariants?.();
  }, [selectedCopyVariantId, copyLoading, copyVariants, refetchCopyVariants]);
  // The option's OWN stored platform decides which ad shell renders — not
  // the concept's declared "Paid on" value — so the two can never disagree
  // about which network's chrome is correct for the copy actually shown.
  const resolvedAdPlatformId: AdPlatformId | null = adCopy?.adPlatform ?? adPlatformId;

  // Reported up so a parent-rendered switcher can show the right value without
  // re-deriving it. Top level, never inside the `isPaid` branch below — a hook
  // in a conditional is how this file would start rendering the wrong platform
  // the first time a concept flips between paid and organic.
  useEffect(() => {
    if (resolvedAdPlatformId) onResolvedAdPlatformChange?.(resolvedAdPlatformId);
  }, [resolvedAdPlatformId, onResolvedAdPlatformChange]);

  const fieldText = (field: 'headline' | 'caption' | 'cta'): string =>
    adCopy?.fields.find((entry) => entry.field === field)?.text?.trim() || '';

  if (isPaid) {
    // Falls back to Meta only when the concept's own platform has no built
    // shell at all (`resolvedAdPlatformId` null) — never silently overrides a
    // real resolved platform the user hasn't touched.
    const displayAdPlatformId: AdPlatformId = selectedAdPlatform ?? resolvedAdPlatformId ?? 'meta';
    const isNativePlatform = displayAdPlatformId === resolvedAdPlatformId;

    /**
     * A 9:16 paid concept previews the STORY placement, not the feed one —
     * the same thing this component's ORGANIC branch has always done via
     * `resolvePlacement(matrixPlatform, aspectRatio)`, finally true on the
     * paid side too.
     *
     * This is also the competitor-validated shape and it is worth being
     * precise about why, because it is easy to over-read: marketer.com's
     * ad-brief preview has a `Feed`/`Story` toggle, and it is a RATIO-PAIRING
     * artifact rather than a placement picker — concept cards there carry
     * paired ratio badges (`1:1`+`4:5` on one, `9:16` on another) and the
     * toggle just re-renders the same creative at the paired ratio. So the
     * ratio decides the placement here; there is deliberately no placement
     * control for the user to set, and adding one would be inventing a
     * Meta targeting concept Mora does not model.
     *
     * Meta only. LinkedIn has no story placement, and a Google search ad has
     * no creative at all.
     */
    const isStoryPlacement = displayAdPlatformId === 'meta' && aspectRatio === '9:16';

    /**
     * The Instagram shell draws only while the user has not asked for a
     * different network. `selectedAdPlatform` is their explicit "how would this
     * read on Meta / LinkedIn / Google" request, and honouring it is the whole
     * point of the switcher — so an override always wins over the concept's own
     * declared Instagram placement.
     */
    const usesInstagramShell =
      selectedAdPlatform === null &&
      adShellId === 'instagram' &&
      // The loaded copy option's OWN stored platform still decides the network,
      // exactly as it does below — a LinkedIn copy row previewing inside
      // Instagram chrome would make the two disagree about which network this
      // copy was written for. Instagram only narrows the shell within Meta.
      (resolvedAdPlatformId === null || resolvedAdPlatformId === 'meta') &&
      !isStoryPlacement;

    const headline = fieldText('headline') || hook?.trim() || '';
    // Before copy exists, show the Concept's hook, never its internal direction as if it were ad text.
    const caption = fieldText('caption') || hook?.trim() || captionFromSummary(summary);
    const ctaText = fieldText('cta') || cta?.trim() || '';
    const isLoading = copyLoading && !adCopy;
    const hasImage = Boolean(imageUrl);

    const substitutionNote = // Instagram and Facebook share one `AdPlatformId`, so the generic
      // native-platform check below cannot see this substitution: the id still
      // says `meta` while the chrome has changed network. Name it explicitly.
      adShellId === 'instagram' && !usesInstagramShell && displayAdPlatformId === 'meta' ? (
        <>
          This concept is set to run on Instagram — previewing how the same copy would read as a
          Facebook feed ad.
        </>
      ) : isNativePlatform ? null : resolvedAdPlatformId ? (
        <>
          This concept is set to run on {AD_PLATFORM_LABEL[resolvedAdPlatformId]} — previewing how
          the same copy would read on {AD_PLATFORM_LABEL[displayAdPlatformId]}.
        </>
      ) : (
        <>
          Mora has no ad preview for {platformRaw ?? 'this platform'} yet — showing how this copy
          would read on {AD_PLATFORM_LABEL[displayAdPlatformId]} instead.
        </>
      );

    let shell: JSX.Element;
    let placementNote: JSX.Element | null = null;
    if (isStoryPlacement) {
      shell = (
        <StoryAdPreview
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          bodyText={caption}
          imageUrl={imageUrl}
          aspectRatio={aspectRatio}
          isLoading={isLoading}
          overlay={overlay}
          cta={ctaText || undefined}
          embedded
        />
      );
      // The headline has nowhere to go in a story ad — the format has one
      // body block and a CTA pill, and silently dropping a field the marketer
      // wrote is exactly the kind of quiet loss this program exists to stop.
      placementNote = headline ? (
        <>A story ad carries no headline field — “{headline}” is not shown here.</>
      ) : null;
    } else if (usesInstagramShell) {
      shell = (
        <InstagramAdPreview
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          caption={caption}
          headline={headline}
          cta={ctaText || 'Learn More'}
          imageUrl={imageUrl}
          imageUrls={imageUrls ? [...imageUrls] : undefined}
          aspectRatio={aspectRatio}
          isLoading={isLoading}
          emptyHint={emptyHint}
          overlay={overlay}
          embedded
        />
      );
      // Instagram's ad template has no headline node in any of its four Figma
      // frames — the CTA row is a label and a chevron. Saying so beats letting
      // a marketer believe a field they wrote is rendering.
      placementNote = headline ? (
        <>An Instagram feed ad carries no headline field — “{headline}” is not shown here.</>
      ) : null;
    } else if (displayAdPlatformId === 'meta') {
      shell = (
        <MetaAdPreview
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          primaryText={caption}
          headline={headline}
          cta={ctaText || 'Learn More'}
          imageUrl={imageUrl}
          aspectRatio={aspectRatio}
          isLoading={isLoading}
          emptyHint={emptyHint}
          overlay={overlay}
          embedded
        />
      );
    } else if (displayAdPlatformId === 'linkedin') {
      shell = (
        <LinkedInAdPreview
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          commentary={caption}
          headline={headline}
          cta={linkedInCtaDisplayLabel(ctaText) ?? undefined}
          imageUrl={imageUrl}
          aspectRatio={aspectRatio}
          isLoading={isLoading}
          overlay={overlay}
          embedded
        />
      );
    } else {
      // google_rsa — text-only search ad, no creative slot at all: showing the
      // artwork here would claim Google Search renders an image it does not.
      // The empty ARRAY, not a synthetic 'No headline yet' asset: the replica
      // already states an empty inventory honestly, and its combination
      // disclaimer counts what it was given — passing a placeholder string
      // made it report one headline that does not exist.
      shell = (
        <GoogleRsaAdPreview
          headlines={headline ? [headline] : []}
          descriptions={caption ? [caption] : []}
        />
      );
      // Belongs OUTSIDE the stage with the substitution note — it is Mora
      // explaining the placement, not part of what Google renders.
      placementNote = (
        <>
          Google Search has no image placement — this concept&apos;s artwork does not appear here.
        </>
      );
    }

    const paidShell = isStoryPlacement ? (
      <div className="mx-auto w-full" style={{ maxWidth: storyPreviewMaxWidth(isMobile) }}>
        {shell}
      </div>
    ) : displayAdPlatformId === 'google_rsa' ? (
      shell
    ) : usesInstagramShell ? (
      // Instagram's feed card is narrower than Facebook's (420 vs 480 natural,
      // `platformNaturalMaxWidth`), and its media is a 4:5 portrait crop rather
      // than a 1.91:1 landscape one, so it fits by its own cap rather than
      // through the Meta chrome-reserve model.
      <div
        className="mx-auto w-full"
        style={{ maxWidth: `min(100%, ${platformNaturalMaxWidth('instagram-post')}px)` }}
      >
        {shell}
      </div>
    ) : (
      <div
        className="mx-auto w-full"
        style={{
          maxWidth: paidPreviewMaxWidth(
            displayAdPlatformId,
            resolvePaidPreviewMediaRatio(displayAdPlatformId, aspectRatio, hasImage, isLoading),
            isMobile,
          ),
        }}
      >
        {shell}
      </div>
    );

    return (
      <div className={cn('mx-auto flex w-full flex-col gap-3', className)}>
        {/* Rendered here ONLY when nobody upstream has taken the control.
            `ConceptDesignBody` does take it, so it can pin the switcher to the
            well's right edge under the Design/Preview toggle — see the
            `onAdPlatformChange` prop's comment. */}
        {!isPlatformControlled ? (
          <div role="group" aria-label="Ad preview platform" className="flex justify-center">
            <SegmentedToggle
              value={displayAdPlatformId}
              onValueChange={(value) => setSelectedAdPlatform(value)}
              options={AD_PLATFORM_OPTIONS}
              variant="default"
              ariaLabel="Ad preview platform"
              size="sm"
            />
          </div>
        ) : null}
        {/* This pane had NO stage — verified live on
            /briefs/:id/concepts/:taskId/design, 2026-08-21, the first visual
            check this surface has ever had. A white replica card sat directly
            on a 99%-white page and its edge was invisible. Every other ad
            surface (/ad-copy, the chat tool-view, the Meta authoring review)
            already put the replica on `bg-preview-stage`; this one was the
            outlier. */}
        <PreviewStage className="rounded-lg">{paidShell}</PreviewStage>
        {placementNote ? (
          <p className="text-center text-detail text-muted-foreground">{placementNote}</p>
        ) : null}
        {substitutionNote ? (
          <p className="text-center text-detail text-muted-foreground">{substitutionNote}</p>
        ) : null}
      </div>
    );
  }

  if (!matrixPlatform) {
    return (
      <div className={cn('flex flex-1 items-center justify-center p-6 text-center', className)}>
        <p className="max-w-xs text-caption text-muted-foreground">
          {platformRaw
            ? `Mora has no placement preview for ${platformRaw} yet.`
            : 'Set a delivery platform on this concept to preview its placement.'}
        </p>
      </div>
    );
  }

  const placement = resolvePlacement(matrixPlatform, aspectRatio);
  if (!placement) {
    return (
      <div className={cn('flex flex-1 items-center justify-center p-6 text-center', className)}>
        <p className="max-w-xs text-caption text-muted-foreground">
          Mora has no placement preview for {platformRaw} yet.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-1 flex-col items-center justify-center gap-2 p-4', className)}>
      {/* Same stage the paid branch above now gets, for the same reason: an
          organic replica is a card too, and a card on a 99%-white page has no
          visible edge. The full-bleed shells (reel/story/tiktok/short) are
          already black and gain little, but a preview pane that changes its
          ground depending on which placement resolved would be the worse
          inconsistency. */}
      <PreviewStage className="w-full rounded-lg">
        <PlatformPreview
          kind={placement.previewKind}
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          caption={hook?.trim() || captionFromSummary(summary)}
          imageUrl={imageUrl}
          aspectRatio={aspectRatio}
          mediaOverlay={overlay}
        />
      </PreviewStage>
      <p className="text-center text-detail text-muted-foreground">
        {placement.placement === 'feed'
          ? 'Feed placement — others crop differently.'
          : `${placement.placement} placement, ${aspectRatio}.`}
      </p>
    </div>
  );
}
